import { ReactNode } from 'react';

import { Alert, Flex, Skeleton } from '@backstage/ui';

import { isNotFoundError } from '@spotify/confidence-graphql';

import { NotFoundContent } from '../Page/Wrapper/presets/ResourceGuard/NotFoundContent';
import BodyBui from './BodyBui';
import CloseButtonBui from './CloseButtonBui';
import HeaderBui from './HeaderBui';

export type ResourceGuardBuiProps = {
  error?: Error | null;
  loading?: boolean;
  loadingFallback?: ReactNode;
  children: ReactNode;
};

const FallbackShell = ({
  children,
  autoHeight,
}: {
  children: ReactNode;
  autoHeight?: boolean;
}) => (
  <>
    <HeaderBui>
      <CloseButtonBui />
    </HeaderBui>
    <BodyBui autoHeight={autoHeight}>{children}</BodyBui>
  </>
);

export const ResourceGuardBui = ({
  error,
  loading,
  loadingFallback,
  children,
}: ResourceGuardBuiProps) => {
  if (error) {
    if (isNotFoundError(error)) {
      return (
        <FallbackShell autoHeight>
          <NotFoundContent variant="resource" fillViewport={false} />
        </FallbackShell>
      );
    }

    return (
      <FallbackShell>
        <Alert status="danger" icon title={error.message} />
      </FallbackShell>
    );
  }

  if (loading) {
    return (
      <FallbackShell>
        {loadingFallback ?? (
          <Flex direction="column" gap="2" p="4">
            <Skeleton width="100%" height={20} />
            <Skeleton width="100%" height={20} />
            <Skeleton width="60%" height={20} />
          </Flex>
        )}
      </FallbackShell>
    );
  }

  return <>{children}</>;
};

ResourceGuardBui.displayName = 'DialogCompositeBui.ResourceGuard';

export default ResourceGuardBui;
